import { ReactNode, useEffect, useCallback, useRef } from "react";
import {
  addUserInfo,
  clearAll,
  logout,
  setError,
  setLoading,
  setAuthChecked,
  setAuthResolving,
  setMfaPending,
} from "@store/slices/authSlice";
import { AUTH_ERROR_EVENT } from "@services/apiClient";
import { updateUserId } from "@store/slices/userSlice";
import { setIsDemoMode } from "@store/slices/viewSlice";
import { loadCurrentUserOrgs, clearOrgs } from "@store/slices/orgsSlice";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { useNavigate } from "react-router-dom";
import { getLatestWorkspaceId, getWorkspaceData } from "@services/api";
import {
  getCurrentAal,
  getSessionUserId,
  listMfaFactors,
  onAuthStateChange,
} from "@services/supabaseAPI";
import { setWorkspaceData } from "@store/slices/workspaceSlice";

interface Props {
  children: ReactNode;
}
const AuthContext = ({ children }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mfaPending = useAppSelector((state) => state.auth.mfaPending);
  const userIdInStore = useAppSelector((state) => state.user.userId);
  const workspaceId = useAppSelector((state) => state.workspace.workspace_id);
  const hasCategories = useAppSelector(
    (state) => (state.workspace.shared_data?.categories?.length ?? 0) > 0
  );
  // workspace_permissions is the only signal that's only ever populated by
  // a real fetch — categories ship with defaults in the slice's initial
  // state, so they can't distinguish "loaded" from "boot-time placeholder."
  // Required for selectIsCurrentUserWorkspaceOwner-gated UI (e.g. the
  // admin panel's Members + Settings tabs) to show on a fresh-tab load
  // where workspace_id was restored from IndexedDB but nothing else was.
  const hasPermissions = useAppSelector(
    (state) => (state.workspace.workspace_permissions?.length ?? 0) > 0
  );

  // Helper function to load workspace data if it's not already loaded
  const loadWorkspaceIfNeeded = useCallback(async () => {
    // Skip the fetch only when the persisted workspace state is also usable.
    // Both checks must pass: categories rules out legacy null-categories
    // persisted state, permissions rules out the post-IndexedDB-restore
    // case where the slice's default categories make the categories check
    // pass spuriously.
    if (workspaceId && hasCategories && hasPermissions) {
      return;
    }

    if (workspaceId) {
      const result = await getWorkspaceData(workspaceId);
      if (result.success) {
        const { workspace_id, workspace_name, user_data, shared_data, workspace_permissions } = result.data;
        dispatch(setWorkspaceData({
          workspace_id,
          workspace_name,
          shared_data,
          user_data,
          workspace_permissions,
        }));
        return;
      }
    }

    const latestResult = await getLatestWorkspaceId();

    if (!latestResult.success || !latestResult.data) {
      dispatch(setError("No accessible workspaces found"));
      return;
    }

    const workspaceResult = await getWorkspaceData(latestResult.data);

    if (!workspaceResult.success) {
      dispatch(setError(workspaceResult.error.message || "No accessible workspaces found"));
      return;
    }

    const { workspace_id, workspace_name, user_data, shared_data, workspace_permissions } = workspaceResult.data;

    dispatch(setWorkspaceData({
      workspace_id,
      workspace_name,
      shared_data,
      user_data,
      workspace_permissions,
    }));
  }, [dispatch, workspaceId, hasCategories, hasPermissions]);

  // Run the post-auth bootstrap (userId, user info, orgs, workspace).
  // Factored out so both initial getSession and the SIGNED_IN handler
  // can call it after the AAL pre-flight has cleared the user for
  // entry. Sets mfaPending=false to be sure we don't leave a stale
  // gate on after a successful step-up.
  const runBootstrap = useCallback(
    async (userId: string) => {
      dispatch(setIsDemoMode(false));
      dispatch(setMfaPending(false));
      dispatch(updateUserId(userId));
      // The user is now authenticated and cleared for entry — release
      // RequireAuth immediately. The workspace load below proceeds in the
      // background (its own spinner), so a slow fetch can't strand routing.
      dispatch(setAuthResolving(false));
      dispatch(addUserInfo(userId));
      dispatch(loadCurrentUserOrgs());
      try {
        await loadWorkspaceIfNeeded();
      } catch (error) {
        dispatch(
          setError(error instanceof Error ? error.message : String(error))
        );
      }
    },
    [dispatch, loadWorkspaceIfNeeded]
  );

  // Decide whether a freshly-authenticated session can boot straight
  // into the app or must complete an MFA challenge first.
  //
  // We can't rely on getAuthenticatorAssuranceLevel.nextLevel alone:
  // immediately after signInWithPassword the SDK's session.user.factors
  // hasn't been hydrated, so nextLevel reads back as aal1 even when a
  // verified TOTP factor exists. listFactors() forces a fresh network
  // call and returns ground truth — if the user has a verified TOTP
  // factor and their current JWT is aal1, they need step-up.
  //
  // RequireAuth gates on userId, so simply not running bootstrap is
  // sufficient to keep the user out of the app.
  const gateOnAal = useCallback(
    async (userId: string) => {
      // Hold RequireAuth from redirecting while we run the AAL/MFA probe
      // and bootstrap: post-login, authChecked is already true but userId
      // isn't set yet. Cleared in runBootstrap the moment userId lands, and
      // in the finally below for the MFA-pending / error / timeout paths.
      dispatch(setAuthResolving(true));
      try {
      // Hard timeout fallback. getCurrentAal and listMfaFactors both
      // go through Supabase JS's internal session lock; if a prior
      // operation (e.g. a late-resolving updateUser PUT from a
      // same-session password change) is still in flight, both will
      // block waiting for the lock — stranding the bootstrap and
      // leaving the user on the landing page after a successful
      // sign-in. If we can't probe within 2s, treat as "no enrolled
      // factor" and proceed; the worst case is that an MFA user
      // briefly bypasses MFA on a route, which the server-side AAL2
      // gate would then catch via step-up. Better than a stuck UX.
      const probe = Promise.all([getCurrentAal(), listMfaFactors()]).then(
        ([aal, factors]) =>
          ({ timedOut: false, current: aal.current, factors } as const)
      );
      const timeout = new Promise<{ timedOut: true }>((resolve) =>
        setTimeout(() => resolve({ timedOut: true }), 2000)
      );
      const result = await Promise.race([probe, timeout]);
      if (!result.timedOut) {
        if (result.factors.length > 0 && result.current !== "aal2") {
          dispatch(setMfaPending(true));
          return;
        }
      }
      await runBootstrap(userId);
      } finally {
        dispatch(setAuthResolving(false));
      }
    },
    [dispatch, runBootstrap]
  );

  // Bootstrap-on-mfa-cleared. The Supabase JS SDK does not reliably
  // emit TOKEN_REFRESHED after mfa.verify in every version, which
  // means relying on the onAuthStateChange listener alone leaves the
  // user stuck in the modal even though their session is now AAL2.
  // Instead, MfaChallengeForm dispatches setMfaPending(false) on a
  // successful verify; this effect catches the true→false transition
  // and runs the deferred bootstrap directly. The userIdInStore guard
  // prevents a duplicate bootstrap if the listener path also fires.
  const prevMfaPending = useRef(mfaPending);
  useEffect(() => {
    if (prevMfaPending.current && !mfaPending && !userIdInStore) {
      (async () => {
        const { userId } = await getSessionUserId();
        if (userId) await runBootstrap(userId);
      })();
    }
    prevMfaPending.current = mfaPending;
  }, [mfaPending, userIdInStore, runBootstrap]);

  // Stash gateOnAal in a ref so the long-lived onAuthStateChange
  // subscription and the initial-getSession effect below can call
  // the current version without keeping gateOnAal in their dep
  // arrays. Every workspace-state change otherwise tears down and
  // re-creates the Supabase subscription, and any auth event firing
  // during that window is silently lost — the race that bit the
  // rapid logout→login flow.
  const gateOnAalRef = useRef(gateOnAal);
  useEffect(() => {
    gateOnAalRef.current = gateOnAal;
  }, [gateOnAal]);

  useEffect(() => {
    let mounted = true;

    async function getSession() {
      const { userId, error } = await getSessionUserId();

      if (!mounted) return;

      if (error) {
        dispatch(setError(error));
        dispatch(setLoading(false));
        dispatch(setAuthChecked(true));
        return;
      }

      if (!userId) {
        dispatch(setAuthChecked(true));
        return;
      }

      // gateOnAal goes through the ref so this effect doesn't
      // re-run every time workspace state changes (which would
      // trigger an unnecessary duplicate bootstrap cycle).
      await gateOnAalRef.current(userId);

      dispatch(setLoading(false));
      dispatch(setAuthChecked(true));
    }

    getSession();

    return () => {
      mounted = false;
    };
    // Run once on mount. dispatch is stable; gateOnAal is read
    // through the ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const subscription = onAuthStateChange(async (event, session) => {
      const user = session?.user;

      switch (event) {
        case "SIGNED_IN": {
          if (user) {
            await gateOnAalRef.current(user.id);
          }
          break;
        }
        case "USER_UPDATED":
        case "TOKEN_REFRESHED": {
          if (user) {
            // A refresh that lands at AAL2 (after the user completed
            // an MFA challenge mid-session) is our cue to run the
            // bootstrap if it was deferred. Otherwise this path keeps
            // userId / user info in sync as before.
            await gateOnAalRef.current(user.id);
          }
          break;
        }
        case "SIGNED_OUT": {
          dispatch(updateUserId(""));
          dispatch(clearOrgs());
          dispatch(clearAll());
          dispatch(setMfaPending(false));
          break;
        }
        case "PASSWORD_RECOVERY": {
          navigate("/resetpassword");
          break;
        }
        default:
          break;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
    // Subscribe ONCE on mount and never tear down. dispatch and
    // navigate are stable references; gateOnAal is read through the
    // ref above so its identity changes don't matter. Re-subscribing
    // on workspace-state changes (the old behavior) created a window
    // where SIGNED_IN / TOKEN_REFRESHED could be lost.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A 401 from the API (emitted as AUTH_ERROR_EVENT by the apiClient
  // interceptor; mfa_required is excluded there) means the session is no
  // longer valid. Sign out and return to the landing page instead of
  // leaving the user on a broken, half-authenticated screen. Guarded on a
  // present userId so a stray 401 before login can't trigger a redirect.
  useEffect(() => {
    const handleAuthError = () => {
      if (!userIdInStore) return;
      dispatch(clearAll());
      dispatch(logout());
      navigate("/");
    };
    window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);
    return () => window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
  }, [userIdInStore, dispatch, navigate]);

  return <>{children}</>;
};

AuthContext.propTypes = {};

export default AuthContext;
